import React, { useEffect, useRef, useState, useCallback } from "react";
import axios from "axios";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import {
  ChevronDown, ChevronUp, CheckCircle2, Circle, FileText,
  Save, BookOpen, AlertCircle, X
} from "lucide-react";
import { useLang } from "../lib/i18n";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const HEARTBEAT_INTERVAL_MS = 5 * 60 * 1000; // 5 dakika
const MIN_PROGRESS_PERCENT = 5;               // % altında heartbeat gönderme

function pickLang(field, lang, fb = "") {
  if (!field) return fb;
  if (typeof field === "string") return field;
  return field[lang] || field.tr || field.en || fb;
}

function formatSec(sec) {
  if (!sec || sec < 0) return "";
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = Math.floor(sec % 60);
  if (h > 0) return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  return `${m}:${String(s).padStart(2, "0")}`;
}

export default function TrainingWatch() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const enrollmentId = searchParams.get("eid");
  const navigate = useNavigate();
  const { lang } = useLang();

  const [course, setCourse] = useState(null);
  const [enrollment, setEnrollment] = useState(null);
  const [progress, setProgress] = useState({});   // {lesson_id: {percent_complete, completed}}
  const [activeLesson, setActiveLesson] = useState(null);
  const [videoUrl, setVideoUrl] = useState(null);
  const [videoLoading, setVideoLoading] = useState(false);
  const [openModules, setOpenModules] = useState({});
  const [note, setNote] = useState("");
  const [noteSaved, setNoteSaved] = useState(false);
  const [showNotes, setShowNotes] = useState(false);
  const [error, setError] = useState(null);

  const videoRef = useRef(null);
  const heartbeatTimerRef = useRef(null);
  const isTabVisible = useRef(true);
  const lastHeartbeatAt = useRef(0);

  // ---- Yükle ----
  useEffect(() => {
    if (!enrollmentId) { setError("Geçersiz erişim bağlantısı."); return; }

    Promise.all([
      axios.get(`${API}/courses/${slug}`),
      axios.get(`${API}/enrollments/${enrollmentId}`),
      axios.get(`${API}/enrollments/${enrollmentId}/progress`),
    ])
      .then(([courseRes, enrollRes, progressRes]) => {
        const c = courseRes.data;
        const e = enrollRes.data;
        const prog = {};
        for (const p of (progressRes.data?.items || [])) {
          prog[p.lesson_id] = p;
        }
        setCourse(c);
        setEnrollment(e);
        setProgress(prog);
        // İlk modülü aç, ilk dersi seç
        const firstMod = c.modules?.[0];
        if (firstMod) {
          setOpenModules({ [firstMod.id]: true });
          const firstLesson = firstMod.lessons?.[0];
          if (firstLesson) selectLesson(firstLesson, enrollmentId);
        }
      })
      .catch((err) => {
        const detail = err?.response?.data?.detail;
        const code = typeof detail === "object" ? detail.code : null;
        if (code === "enrollment_expired") setError("Eğitim erişim süreniz doldu.");
        else if (code === "enrollment_inactive") setError("Bu eğitim için aktif kaydınız bulunmuyor.");
        else setError("Eğitim yüklenemedi. Lütfen tekrar deneyin.");
      });
  }, [slug, enrollmentId, selectLesson]);

  // ---- Sekme değişince video duraklat ----
  useEffect(() => {
    const handleVisibility = () => {
      isTabVisible.current = document.visibilityState === "visible";
      if (!isTabVisible.current && videoRef.current) {
        videoRef.current.pause();
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () => document.removeEventListener("visibilitychange", handleVisibility);
  }, []);

  // ---- Heartbeat ----
  const sendHeartbeat = useCallback(async () => {
    const video = videoRef.current;
    if (!video || !activeLesson || !enrollmentId) return;
    if (!isTabVisible.current) return;   // sekme gizliyse heartbeat gönderme

    const watched = video.currentTime;
    const total = video.duration || 0;
    const percent = total > 0 ? (watched / total) * 100 : 0;

    if (percent < MIN_PROGRESS_PERCENT) return;

    const now = Date.now();
    if (now - lastHeartbeatAt.current < HEARTBEAT_INTERVAL_MS) return;
    lastHeartbeatAt.current = now;

    try {
      const { data } = await axios.post(`${API}/enrollments/${enrollmentId}/heartbeat`, {
        lesson_id: activeLesson.id,
        watched_seconds: watched,
        total_seconds: total,
      });
      setProgress((p) => ({
        ...p,
        [activeLesson.id]: {
          percent_complete: data.percent_complete,
          completed: data.completed,
        },
      }));
      if (data.overall_percent >= 100) {
        toast.success("Tebrikler! Eğitimi tamamladınız.");
      }
    } catch (_) { /* sessizce geç */ }
  }, [activeLesson, enrollmentId]);

  // Heartbeat timer
  useEffect(() => {
    heartbeatTimerRef.current = setInterval(sendHeartbeat, HEARTBEAT_INTERVAL_MS);
    return () => clearInterval(heartbeatTimerRef.current);
  }, [sendHeartbeat]);

  // Video bitince de heartbeat
  const handleVideoEnded = () => sendHeartbeat();

  // ---- Ders seç ----
  const selectLesson = useCallback(async (lesson, eid = enrollmentId) => {
    setActiveLesson(lesson);
    setVideoUrl(null);
    setVideoLoading(true);
    setNote("");
    setNoteSaved(false);

    // Notu yükle
    try {
      const notesRes = await axios.get(`${API}/enrollments/${eid}/notes`);
      const lessonNote = (notesRes.data || []).find((n) => n.lesson_id === lesson.id);
      setNote(lessonNote?.content || "");
    } catch (_) {}

    // Signed URL al
    try {
      const { data } = await axios.get(`${API}/enrollments/${eid}/lesson/${lesson.id}/url`);
      setVideoUrl(data.url);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Video yüklenemedi.");
    } finally {
      setVideoLoading(false);
    }
  }, [enrollmentId]);

  // URL 15 dk'da bir yenile
  useEffect(() => {
    if (!activeLesson || !enrollmentId) return;
    const timer = setInterval(async () => {
      try {
        const { data } = await axios.get(`${API}/enrollments/${enrollmentId}/lesson/${activeLesson.id}/url`);
        setVideoUrl(data.url);
      } catch (_) {}
    }, 14 * 60 * 1000); // 14 dk
    return () => clearInterval(timer);
  }, [activeLesson, enrollmentId]);

  // ---- Not kaydet ----
  const saveNote = async () => {
    if (!activeLesson || !enrollmentId) return;
    try {
      await axios.put(`${API}/enrollments/${enrollmentId}/notes/${activeLesson.id}`, {
        lesson_id: activeLesson.id,
        content: note,
      });
      setNoteSaved(true);
      setTimeout(() => setNoteSaved(false), 2000);
    } catch (_) {
      toast.error("Not kaydedilemedi.");
    }
  };

  // ---- Hata ekranı ----
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-cream-100 p-6">
        <div className="surface-card p-10 max-w-md text-center">
          <AlertCircle className="w-10 h-10 mx-auto mb-4 text-terracotta-400" strokeWidth={1.4} />
          <h2 className="font-serif text-2xl text-ink mb-3">{error}</h2>
          <button onClick={() => navigate("/trainings")} className="btn-primary mt-2">Eğitimlere Dön</button>
        </div>
      </div>
    );
  }

  if (!course || !enrollment) {
    return <div className="min-h-screen flex items-center justify-center text-ink/55">Yükleniyor…</div>;
  }

  const overallPercent = enrollment.overall_percent || 0;

  return (
    <div className="min-h-screen bg-ink text-cream-100 flex flex-col" data-testid="training-watch-page">
      {/* Top bar */}
      <header className="flex items-center justify-between px-5 py-3 border-b border-cream-100/10 bg-ink shrink-0">
        <div className="flex items-center gap-3 min-w-0">
          <button onClick={() => navigate("/trainings")} className="text-cream-100/50 hover:text-cream-100 shrink-0">
            <X className="w-5 h-5" strokeWidth={1.5} />
          </button>
          <span className="font-serif italic text-terracotta-400 text-lg shrink-0">inTouch</span>
          <span className="text-cream-100/40 mx-1 shrink-0">·</span>
          <span className="text-sm text-cream-100/70 truncate">{pickLang(course.title, lang)}</span>
        </div>
        {/* Progress bar */}
        <div className="hidden md:flex items-center gap-3">
          <div className="w-32 h-1.5 bg-cream-100/20 rounded-full overflow-hidden">
            <div
              className="h-full bg-terracotta-400 rounded-full transition-all"
              style={{ width: `${overallPercent}%` }}
            />
          </div>
          <span className="text-xs text-cream-100/50">%{Math.round(overallPercent)}</span>
        </div>
      </header>

      {/* Main */}
      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar — modüller */}
        <aside className="hidden lg:flex flex-col w-72 shrink-0 border-r border-cream-100/10 overflow-y-auto bg-ink/95">
          <div className="px-5 py-4 border-b border-cream-100/10">
            <p className="text-xs uppercase tracking-[0.22em] text-cream-100/45">İçerik</p>
          </div>
          <div className="flex-1 overflow-y-auto">
            {(course.modules || []).map((m, mi) => {
              const isOpen = openModules[m.id];
              return (
                <div key={m.id} className="border-b border-cream-100/10">
                  <button
                    onClick={() => setOpenModules((p) => ({ ...p, [m.id]: !p[m.id] }))}
                    className="w-full flex items-center justify-between px-5 py-3 text-left hover:bg-cream-100/5 transition-colors"
                  >
                    <span className="text-sm font-medium text-cream-100/80">
                      {pickLang(m.title, lang) || `Modül ${mi + 1}`}
                    </span>
                    {isOpen
                      ? <ChevronUp className="w-3.5 h-3.5 text-cream-100/40 shrink-0" strokeWidth={1.5} />
                      : <ChevronDown className="w-3.5 h-3.5 text-cream-100/40 shrink-0" strokeWidth={1.5} />
                    }
                  </button>
                  {isOpen && (
                    <ul>
                      {(m.lessons || []).map((l, li) => {
                        const prog = progress[l.id];
                        const isActive = activeLesson?.id === l.id;
                        const isDone = prog?.completed || prog?.percent_complete >= 90;
                        return (
                          <li key={l.id}>
                            <button
                              onClick={() => selectLesson(l)}
                              className={`w-full flex items-start gap-3 px-5 py-2.5 text-left transition-colors ${
                                isActive ? "bg-terracotta-400/20 text-cream-100" : "hover:bg-cream-100/5 text-cream-100/65"
                              }`}
                            >
                              {isDone
                                ? <CheckCircle2 className="w-4 h-4 text-sage-400 shrink-0 mt-0.5" strokeWidth={1.5} />
                                : <Circle className="w-4 h-4 text-cream-100/20 shrink-0 mt-0.5" strokeWidth={1.5} />
                              }
                              <span className="text-sm leading-snug flex-1">
                                {pickLang(l.title, lang) || `Ders ${li + 1}`}
                              </span>
                              {l.duration_sec > 0 && (
                                <span className="text-xs text-cream-100/30 shrink-0">{formatSec(l.duration_sec)}</span>
                              )}
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              );
            })}
          </div>
        </aside>

        {/* Video + Notlar */}
        <main className="flex-1 overflow-y-auto flex flex-col">
          {/* Video */}
          <div className="bg-black relative" style={{ aspectRatio: "16/9", maxHeight: "60vh" }}>
            {videoLoading && (
              <div className="absolute inset-0 flex items-center justify-center text-cream-100/40 text-sm">
                Video yükleniyor…
              </div>
            )}
            {videoUrl && (
              <video
                ref={videoRef}
                key={videoUrl}
                src={videoUrl}
                controls
                controlsList="nodownload"
                onContextMenu={(e) => e.preventDefault()}
                onEnded={handleVideoEnded}
                className="w-full h-full"
                style={{ maxHeight: "60vh" }}
              />
            )}
            {!videoLoading && !videoUrl && (
              <div className="absolute inset-0 flex items-center justify-center">
                <BookOpen className="w-12 h-12 text-cream-100/20" strokeWidth={1} />
              </div>
            )}
          </div>

          {/* Ders başlığı + kaynaklar */}
          <div className="px-6 pt-5 pb-4 border-b border-cream-100/10">
            <h2 className="font-serif text-2xl text-cream-100">
              {activeLesson ? pickLang(activeLesson.title, lang) : "Ders seçin"}
            </h2>

            {/* Kaynaklar */}
            {activeLesson && (activeLesson.resources || []).length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2">
                {activeLesson.resources.map((res, ri) => (
                  <button
                    key={ri}
                    onClick={async () => {
                      try {
                        const { data } = await axios.get(
                          `${API}/enrollments/${enrollmentId}/resource/${activeLesson.id}/${ri}/url`
                        );
                        window.open(data.url, "_blank");
                      } catch (_) { toast.error("Kaynak açılamadı."); }
                    }}
                    className="inline-flex items-center gap-1.5 text-xs bg-cream-100/10 hover:bg-cream-100/20 text-cream-100/70 px-3 py-1.5 rounded-full transition-colors"
                  >
                    <FileText className="w-3 h-3" strokeWidth={1.5} />
                    {res.title || `Kaynak ${ri + 1}`}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Notlar */}
          <div className="px-6 py-4 flex-1">
            <div className="flex items-center justify-between mb-3">
              <button
                onClick={() => setShowNotes((p) => !p)}
                className="flex items-center gap-2 text-sm text-cream-100/60 hover:text-cream-100"
              >
                <FileText className="w-4 h-4" strokeWidth={1.5} />
                Notlarım
                {showNotes
                  ? <ChevronUp className="w-3.5 h-3.5" strokeWidth={1.5} />
                  : <ChevronDown className="w-3.5 h-3.5" strokeWidth={1.5} />
                }
              </button>
              {showNotes && (
                <button
                  onClick={saveNote}
                  className="inline-flex items-center gap-1.5 text-xs text-sage-400 hover:text-sage-300"
                >
                  <Save className="w-3.5 h-3.5" strokeWidth={1.5} />
                  {noteSaved ? "Kaydedildi ✓" : "Kaydet"}
                </button>
              )}
            </div>
            {showNotes && (
              <textarea
                value={note}
                onChange={(e) => { setNote(e.target.value); setNoteSaved(false); }}
                placeholder="Bu ders için notlarınızı buraya yazın…"
                rows={8}
                className="w-full bg-cream-100/5 border border-cream-100/15 rounded-xl p-4 text-sm text-cream-100/80 font-light leading-relaxed resize-none focus:outline-none focus:border-terracotta-400/50 focus:bg-cream-100/8 transition-colors"
              />
            )}
          </div>
        </main>
      </div>
    </div>
  );
}
